import React from "react";
import { theme } from "antd";
import { useTranslate } from "@refinedev/core";
import { TrashIcon, WarningIcon } from "@phosphor-icons/react";
import { Button, Modal, Stack } from "@libertify/ui";
import { WarningChip, Message } from "./ConfirmRemovalModal.styles";

/**
 * Key prefix holding this removal's `title` (interpolating `name`), `message`,
 * `confirm` and `cancel`. Typed as a union rather than a bare string so a typo
 * fails the build instead of rendering a raw key at the user.
 */
type ConfirmRemovalKey =
  | "createExperience.removeAddonModal"
  | "createExperience.deletePlaybookModal";

interface ConfirmRemovalModalProps {
  open: boolean;
  i18nKey: ConfirmRemovalKey;
  /** Interpolated into the title, and bolded ahead of the message. */
  name: string;
  /** The removal is in flight — the modal stays open until it resolves. */
  isPending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation before removing something: an add-on from the experience being
 * built, or one of the organization's playbooks.
 *
 * NOTE on the copy: each `message` deliberately starts mid-sentence, because
 * the body renders the bolded name ahead of it ("<b>Quiz</b> won't be part of
 * this experience…"). A new usage that reads oddly is almost always a message
 * key that repeats the name.
 */
export const ConfirmRemovalModal: React.FC<ConfirmRemovalModalProps> = ({
  open,
  i18nKey,
  name,
  isPending = false,
  onCancel,
  onConfirm,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  return (
    <Modal
      open={open}
      onClose={onCancel}
      size="medium"
      title={t(`${i18nKey}.title`, { name })}
      styles={{
        header: { display: "flex", alignItems: "center" },
        footer: { background: token.colorBgContainer },
      }}
      footer={
        <Stack fullWidth justify="end" align="center" gap={12}>
          <Button variant="outlined" size="m" onClick={onCancel}>
            {t(`${i18nKey}.cancel`)}
          </Button>
          <Button
            variant="danger"
            size="m"
            loading={isPending}
            leftIcon={<TrashIcon size={16} />}
            onClick={onConfirm}
          >
            {t(`${i18nKey}.confirm`)}
          </Button>
        </Stack>
      }
    >
      <Stack align="start" gap={16}>
        <WarningChip $token={token}>
          <WarningIcon size={24} weight="fill" />
        </WarningChip>
        <Message $token={token}>
          <strong>{name}</strong> {t(`${i18nKey}.message`)}
        </Message>
      </Stack>
    </Modal>
  );
};
